/**
 * Helpers for pasting Excel cells into TinyMCE.
 *
 * Excel's clipboard HTML keeps cell borders in a <style> block (.xl65 { border: .5pt solid windowtext }) and only
 * puts the class on the cell. TinyMCE pastes only the body, dropping that block, so the table arrives without
 * borders. These helpers read the borders from the raw clipboard HTML so they can be put back on the pasted cells
 * as inline styles, which also survive in the sent email.
 */
import { inlineStyleSheets } from "./htmlCleanup";

const EXCEL_MARKER = /urn:schemas-microsoft-com:office:excel|content="?Excel\.Sheet/i;

export type CellBorders = [property: string, value: string][];

/** The border declarations of each table cell in Excel clipboard HTML, in document order; null when it is not from Excel. */
export function readExcelCellBorders(clipboardHtml: string): CellBorders[] | null {
    if (!EXCEL_MARKER.test(clipboardHtml)) return null;

    const doc = new DOMParser().parseFromString(clipboardHtml, "text/html");
    inlineStyleSheets(doc);
    return Array.from(doc.querySelectorAll<HTMLElement>("td, th")).map(cell =>
        Array.from(cell.style)
            .filter(prop => prop.startsWith("border") && !prop.startsWith("border-image"))
            // windowtext is a system colour: it turns white in dark mode and not every mail client knows it
            .map(prop => [prop, cell.style.getPropertyValue(prop).replace(/windowtext/gi, "#000000")]),
    );
}

// given to every cell of a pasted range that has no borders, since Excel's gridlines are not copied
const DEFAULT_BORDER = "1px solid #000000";

function hasVisibleBorder(borders: CellBorders): boolean {
    const props = new Map(borders);
    return ["top", "right", "bottom", "left"].some(side => {
        const style = props.get(`border-${side}-style`);
        const width = props.get(`border-${side}-width`);
        return !!style && style !== "none" && style !== "hidden" && (width === undefined || parseFloat(width) !== 0);
    });
}

/**
 * Puts borders read by readExcelCellBorders back on the pasted cells; skipped if the pasted table has different cells.
 * A range with no borders at all gets a plain 1px black grid instead; one with any border keeps exactly Excel's.
 */
export function applyExcelCellBorders(root: ParentNode, borders: CellBorders[]) {
    const cells = root.querySelectorAll<HTMLElement>("td, th");
    if (cells.length !== borders.length) return;

    if (!borders.some(hasVisibleBorder)) {
        cells.forEach(cell => (cell.style.border = DEFAULT_BORDER));
        // without collapsing, neighbouring cells would draw a double line
        root.querySelectorAll<HTMLElement>("table").forEach(table => (table.style.borderCollapse = "collapse"));
        return;
    }

    cells.forEach((cell, i) => {
        for (const [prop, value] of borders[i]) {
            cell.style.setProperty(prop, value);
        }
    });
}
